import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import './TrabalheConosco.css';
import { SiteHeader, SiteFooter } from '../components/Shared';
import { getStaffAvailabilityInfo, submitStaffAvailability } from '../api';

const WEEKDAY_NAMES = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];

function formatShortDate(iso) {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}

function weekdayName(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return WEEKDAY_NAMES[new Date(y, m - 1, d).getDay()];
}

function TrabalheConoscoDisponibilidade() {
  const { candidate, token } = useParams();

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [busyProduction, setBusyProduction] = useState(null);
  const [message, setMessage] = useState(null);

  const load = useCallback(() => {
    setLoading(true);

    getStaffAvailabilityInfo(candidate, token)
      .then((response) => {
        setStatus(response.status);
        if (response.status === 'ok') setAvailability(response.availability);
      })
      .finally(() => setLoading(false));
  }, [candidate, token]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleAction(day, action) {
    const confirmText = action === 'cancel'
      ? `Cancelar sua disponibilidade de ${weekdayName(day.date)} (${formatShortDate(day.date)})? O gestor será avisado.`
      : `Confirmar disponibilidade para ${weekdayName(day.date)} (${formatShortDate(day.date)})?`;

    if (!window.confirm(confirmText)) return;

    setBusyProduction(day.production);
    setMessage(null);

    try {
      const response = await submitStaffAvailability(candidate, token, action, day.production);

      if (response.availability) setAvailability(response.availability);

      setMessage(
        response.status === 'success'
          ? { type: 'success', text: 'Recebido! Sua escolha foi registrada e o gestor foi avisado.' }
          : { type: 'error', text: (response.errors || ['Não foi possível concluir.']).join(' ') }
      );
    } catch (e) {
      setMessage({ type: 'error', text: 'Não foi possível concluir. Tente novamente.' });
    } finally {
      setBusyProduction(null);
    }
  }

  const days = availability && availability.days ? availability.days : [];
  const selectedCount = availability ? availability.selected_count : 0;
  const maxPerWeek = availability ? availability.max_per_week : 2;

  return (
    <div className="App">
      <SiteHeader />

      <main id="conteudo" className="tc">
        <div className="tc__shell tc__shell--narrow">
          {loading && <p className="tc__copy">Carregando...</p>}

          {!loading && status === 'invalid' && (
            <div className="tc__alert tc__alert--error" role="alert">
              <span className="tc__alert__title">Link inválido ou expirado.</span>
              <p>Peça pra nossa equipe gerar um novo link pra você.</p>
            </div>
          )}

          {!loading && status === 'ok' && availability && (
            <>
              <section className="tc__intro">
                <span className="tc__eyebrow">Olá, {availability.name}</span>
                <h1 className="tc__title">Marque sua disponibilidade</h1>
                <p className="tc__lead">
                  Dias disponíveis de {formatShortDate(availability.start)} a {formatShortDate(availability.end)} — escolha
                  até {maxPerWeek} dias por semana para trabalhar.
                </p>
              </section>

              <div className="tc__alert tc__alert--info" role="status">
                <span className="tc__alert__title">{selectedCount} de {maxPerWeek} dias selecionados nesta semana</span>
              </div>

              {message && (
                <div className={`tc__alert tc__alert--${message.type}`} role={message.type === 'error' ? 'alert' : 'status'}>
                  <span className="tc__alert__title">{message.type === 'success' ? 'Recebido!' : 'Não foi possível concluir.'}</span>
                  {message.type === 'error' && <p>{message.text}</p>}
                </div>
              )}

              <div className="tc__day-list">
                {days.length === 0 && (
                  <p className="tc__copy">Nenhuma produção com vagas abertas para a próxima semana no momento.</p>
                )}

                {days.map((day) => {
                  const canSelect = !day.selected && day.remaining > 0 && selectedCount < maxPerWeek;
                  const blocked = !day.selected && day.remaining > 0 && selectedCount >= maxPerWeek;
                  const busy = busyProduction === day.production;

                  return (
                    <div className={`tc__day-card${day.selected ? ' tc__day-card--selected' : ''}`} key={day.production}>
                      <div className="tc__day-card__info">
                        <span className="tc__day-card__weekday">{weekdayName(day.date)} — {formatShortDate(day.date)}</span>
                        <span className="tc__day-card__meta">
                          {day.selected
                            ? 'Você está confirmado nesse dia.'
                            : blocked
                              ? 'Limite de dias da semana atingido.'
                              : `${day.remaining} ${day.remaining === 1 ? 'vaga restante' : 'vagas restantes'}`}
                        </span>
                      </div>

                      {day.selected ? (
                        <button
                          type="button"
                          className="tc__day-button tc__day-button--outline"
                          disabled={busy}
                          onClick={() => handleAction(day, 'cancel')}
                        >
                          {busy ? 'Aguarde...' : 'Cancelar'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="tc__day-button"
                          disabled={!canSelect || busy}
                          onClick={() => handleAction(day, 'select')}
                        >
                          {busy ? 'Aguarde...' : 'Selecionar'}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

export default TrabalheConoscoDisponibilidade;
